/**
 * Appearance: dark by default, with light and "match my device" still offered.
 *
 * Dark is the default rather than the system preference for two reasons that
 * belong to this product specifically. Most of what a family does here happens
 * in the evening, after school and after work, when a bright screen in a child's
 * bedroom is the wrong thing to hand them. And the palette was drawn for it:
 * adire indigo over a near-black ground is the colour this app actually wants
 * to be, with light as the alternative rather than the other way round.
 *
 * A stored choice always wins. Nobody who picks light should be put back into
 * dark by an update.
 */

export type Theme = 'dark' | 'light' | 'system';

const KEY = 'iran.theme.v1';
/** Catalogue keys, not finished words: appearance reads in the interface language. */
export const THEMES: Array<{ value: Theme; key: string }> = [
  { value: 'dark', key: 'appearance.dark' },
  { value: 'light', key: 'appearance.light' },
  { value: 'system', key: 'appearance.system' },
];

export function readTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'dark' || v === 'light' || v === 'system') return v;
  } catch {
    // Private browsing, or storage switched off. Fall through to the default.
  }
  return 'dark';
}

/**
 * `system` removes the attribute entirely so the stylesheet's
 * prefers-color-scheme block takes over; the other two pin it.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  // Keep the browser chrome (address bar, notch area) in step with the page.
  // Without this the phone's own UI stays light while the app goes dark, which
  // reads as a rendering bug rather than a choice.
  const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  for (const el of document.querySelectorAll('meta[name="theme-color"]')) el.remove();
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = dark ? '#080b22' : '#efecf8';
  document.head.appendChild(meta);
}

export function writeTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // Not fatal: the choice holds for this visit and is simply not remembered.
  }
  applyTheme(theme);
  for (const fn of listeners) fn(theme);
}

const listeners = new Set<(t: Theme) => void>();
export function onThemeChange(fn: (t: Theme) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Re-apply on OS change, but only while the person is actually on "system". */
export function watchSystemTheme(): void {
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (readTheme() === 'system') applyTheme('system');
  });
}
